import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
    BadRequest,
    Config,
    DEFAULT_VOICE_INDEX,
    DeviceConfig,
    dummyEnLanguage,
    dummyEsLanguage,
    Input,
    Language,
    Output,
    Recognition,
    RecognizableAndTranslatableLanguage,
    SERVER_URL,
    SESSION_URL,
    SessionData,
    SessionStates,
    SpeechTokenData,
    TranslatableAndSpeakableLanguage,
    TranslationResult,
    TranslationsBody
} from '../contexts/Configuration';
import {DeviceType} from '../components/Controler';
import {getSessionContext} from '../contexts/SessionResolver';
import {UUID} from 'crypto';
import {RosettaClient} from '../api/RosettaClient';

// const MAX_HISTORY_DURATION_IN_MILLIS: number = 1000 * 30;//30 seconds
const MAX_HISTORY_DURATION_IN_MILLIS: number = 1000 * 60 * 4;//4 minutos

type InitialState = {
    translationApiKey: string | null,
    speechApiKey: string | null,

    input: Input | null,
    outputs: Output[],

    preSelectedInputLanguage: string | null,
    preSelectedOutputLanguages: { [key: string]: string },

    preSelectedInputDevice: string | null,
    preSelectedOutputDevices: { [key: string]: string }

    preSelectedConfig: Config,

    outputsPace: number,
    autoDetectLang: boolean,
    sessionDurationInMins: number,
    broadcastEnabled: boolean,

    modelType: string,

    alreadySelected: string[],
    transcriptionPanels: { id: string, isExtra: boolean }[],
    canStartSession: boolean,
    sessionState: SessionStates,
    currentSessionId: UUID | null,
    currentSessionAPIToken: string | null,
    recognizedTexts: Recognition[],
    currentRecognition: string | null,
    currentTranslations: Map<string, string>,
    translations: Map<string, { timestamp: number, text: string }[]>,
    sessionStarted: boolean,
    availableOutputDevicesAmount: number,
    onTestMode: boolean
}

const initialState: InitialState = {
    translationApiKey: null,
    speechApiKey: null,

    input: null, //TODO sacar del LocalStorage
    outputs: [],

    preSelectedInputLanguage: (JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config).inputLanguage || null,
    preSelectedInputDevice: (JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config).inputDevice || null,

    preSelectedOutputLanguages: (JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config).outputLanguages || {},
    preSelectedOutputDevices: (JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config).outputDevices || {},

    preSelectedConfig: JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config,

    outputsPace: 1.20,
    autoDetectLang: false,
    sessionDurationInMins: (JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config).sessionDuration || 60 * 2, //default 2hs
    broadcastEnabled: false,

    modelType: 'defaultModel',

    alreadySelected: [],

    // transcriptionPanels: [{id: 'panel_1', isExtra: false}],
    transcriptionPanels: Object.keys(
        (JSON.parse(localStorage.getItem('preSelectedConfig') || '{}') as Config).outputLanguages || {'panel_1': ''}
    )
        .map(panelId => ({id: panelId, isExtra: panelId !== 'panel_1'})),

    canStartSession: false,//input and at last 1 output
    sessionStarted: false, //flag
    sessionState: SessionStates.LOADING,//state
    currentSessionId: null,
    currentSessionAPIToken: null,

    recognizedTexts: [],
    currentRecognition: null,
    currentTranslations: new Map<string, string>(),
    translations: new Map<string, { timestamp: number, text: string }[]>(),

    availableOutputDevicesAmount: 0,

    onTestMode: true
}

export type SessionStartedResponse =
    | (Omit<Response, 'json'> & {
    status: 201 | 200;
    json: () => SessionData | PromiseLike<SessionData>;
})
    | (Omit<Response, 'json'> & {
    status: 400;
    json: () => BadRequest | PromiseLike<BadRequest>;
});

export type SpeechTokenRefreshResponse =
    | (Omit<Response, 'json'> & {
    status: 201 | 200;
    json: () => SpeechTokenData | PromiseLike<SpeechTokenData>;
})
    | (Omit<Response, 'json'> & {
    status: 400;
    json: () => BadRequest | PromiseLike<BadRequest>;
});


export const validateAndStartSession = createAsyncThunk<
    SessionData,
    { input: Input, outputs: Output[], sessionDurationInMins: number, broadcastEnabled: boolean, token: string },
    { rejectValue: string }
>(
    'session/start',
    async (params, {rejectWithValue}) => {
        return await RosettaClient.startSession(params.input, params.outputs, params.sessionDurationInMins, params.broadcastEnabled, params.token)
            .catch(error => {
                return rejectWithValue(error.message);
            });
    }
)

export const stopCurrentSession = createAsyncThunk<
    {},
    { token: string, sessionId: UUID | null },
    { rejectValue: string }
>(
    'session/stop',
    async (params, {rejectWithValue}) => {
        // console.log('[stopCurrentSession]', params)
        if (params.sessionId === null) {
            console.log('[stopCurrentSession] Error ACA', params)
            return rejectWithValue('Unknown required data of on process session')
        }

        return await fetch(`${SERVER_URL}${SESSION_URL}/${params.sessionId}`, {
            method: 'PATCH',
            headers: {
                Authorization: `Bearer ${params.token}`
            }
        })
            .then(response => {
                return {};
            })
            .catch(error => {
                return rejectWithValue(error.message)
            })
    }
)

export const sendTranslations = createAsyncThunk<
    {},
    { token: string, sessionId: UUID | null, translations: TranslationsBody },
    { rejectValue: string }
>(
    'session/translations',
    async (params, {rejectWithValue}) => {
        if (params.sessionId === null) {
            console.log('[sendTranslations] Error ACA', params)
            return rejectWithValue('Missing required data of on process session');
        }

        if (!params.translations) {
            return rejectWithValue('Token not available');
        }

        return await fetch(`${SERVER_URL}${SESSION_URL}/${params.sessionId}/translation`, {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
                Authorization: `Bearer ${params.token}`
            },
            body: JSON.stringify(params.translations)
        }).then(response => {
            if (response.ok) {
                console.log('SENT OK!')
                return {};
            } else {
                console.log('SENT FAIL to sent translations to server:', response.json())
                throw new Error('FAIL!!!!!!!')
            }
        }).catch(error => {
            console.log('SENT FAIL to sent translations to server!', error)
            return rejectWithValue(error.message)
        })
    }
)

export const updateSession = createAsyncThunk<
    {},
    { token: string, sessionId: UUID, config: { duration?: number } },
    { rejectValue: string }
>(
    'session/update',
    async (params, {rejectWithValue}) => {
        if (params.sessionId === null) {
            console.log('[updateSession] Error ACA', params)
            return rejectWithValue('Missing required data of on process session');
        }

        return await fetch(`${SERVER_URL}${SESSION_URL}/${params.sessionId}/config`, {
            method: 'PATCH',
            headers: {
                'content-type': 'application/json',
                Authorization: `Bearer ${params.token}`
            },
            body: JSON.stringify(params.config)
        }).then(response => {
            if (response.ok) {
                console.log('SENT OK!')
                return {};
            } else {
                console.log('FAIL on session config update:', response.json())
                throw new Error('FAIL!!!!!!!')
            }
        }).catch(error => {
            console.log('FAIL on session config update:', error)
            return rejectWithValue(error.message)
        })
    }
)


const sessionSlice = createSlice({
    name: 'session',
    initialState,
    reducers: {
        setApiKey(state, action: PayloadAction<{ translationKey: string | null, speechKey: string | null }>) {
            state.translationApiKey = action.payload.translationKey;
            state.speechApiKey = action.payload.speechKey;

            console.log('SETTEADAS API KEYS');
            // if (action.payload) {
            //     localStorage.setItem('apiKey', action.payload);
            // } else {
            //     localStorage.removeItem('apiKey');
            // }
        },
        setInitialOutputDevicesAmount(state, action: PayloadAction<number>) {
            state.availableOutputDevicesAmount = action.payload;
        },
        updateCanStartSession(state, action: PayloadAction<boolean>) {
            state.canStartSession = action.payload;
        },
        updateSessionState(state, action: PayloadAction<{ state: SessionStates }>) {
            // if (state.canStartSession) {
            state.sessionState = action.payload.state;
            // }

            const isStarting = state.sessionState === SessionStates.ON;
            if (isStarting) {
                state.recognizedTexts = [];
                state.currentRecognition = null;
                state.translations = new Map<string, { timestamp: number, text: string }[]>();
                state.currentTranslations = new Map<string, string>();
            }
            // console.log('---> sessionStarted: ', isStarting)
            state.sessionStarted = isStarting;
        },

        addPanel(state, action: PayloadAction<string>) {
            state.transcriptionPanels.push({id: action.payload, isExtra: true});

        },
        removePanel(state, action: PayloadAction<string>) {
            const panelId = action.payload;

            state.outputs = state.outputs.filter(output => panelId !== output.id);
            state.transcriptionPanels = state.transcriptionPanels.filter(panel => panelId !== panel.id);

            //UPDATING CONFIG
            const preSelectedOutputLanguages = state.preSelectedConfig.outputLanguages || {};
            delete preSelectedOutputLanguages[panelId];
            const preSelectedOutputDevices = state.preSelectedConfig.outputDevices || {};
            delete preSelectedOutputDevices[panelId];

            state.preSelectedConfig = {
                ...state.preSelectedConfig,
                outputLanguages: preSelectedOutputLanguages,
                outputDevices: preSelectedOutputDevices
            };
            localStorage.setItem('preSelectedConfig', JSON.stringify(state.preSelectedConfig));

            //UPDATING particular values
            state.preSelectedOutputLanguages = preSelectedOutputLanguages;
            state.preSelectedOutputDevices = preSelectedOutputDevices;
        },

        updatePanelConfiguration(state, action: PayloadAction<DeviceConfig>) {
            // console.log("3-->", action.payload)
            // if (state.sessionState === SessionStates.LOADING) {
            //     // console.log('On state LOADING... returning!')
            // }

            // console.log(trans)
            const panelId = action.payload.panelId;

            const device = action.payload.device;
            const language = action.payload.language;
            const gain = action.payload.gain === undefined || action.payload.gain === null ? 0.5 : action.payload.gain;
            const type = action.payload.type;

            if (type === DeviceType.IN) {
                const lang = language as RecognizableAndTranslatableLanguage;
                const autoDetectEnabled = action.payload.autoDetectLang || false;
                state.input = {
                    id: panelId,
                    device: device,
                    language: language,
                    gain: gain,
                    configComplete: !!(device && (language || autoDetectEnabled)),
                    autoDetect: autoDetectEnabled
                } as Input;

                if (lang?.recognitionKey) { //Siempre guardo el ultimo elegido
                    const selected = language as RecognizableAndTranslatableLanguage;
                    const preSelectedInputLanguageId = /*selected.translationKey + ':' +*/ selected.recognitionKey;

                    //UPDATING CONFIG
                    state.preSelectedConfig = {...state.preSelectedConfig, inputLanguage: preSelectedInputLanguageId};
                    localStorage.setItem('preSelectedConfig', JSON.stringify(state.preSelectedConfig));

                    //UPDATING particular value
                    state.preSelectedInputLanguage = preSelectedInputLanguageId;
                }

                if (device?.deviceId) {
                    //UPDATING CONFIG
                    state.preSelectedConfig = {...state.preSelectedConfig, inputDevice: device.deviceId};
                    localStorage.setItem('preSelectedConfig', JSON.stringify(state.preSelectedConfig));

                    //UPDATING particular value
                    state.preSelectedInputDevice = device.deviceId;
                }
            }

            if (type === DeviceType.OUT && !!panelId) {
                let filtered = state.outputs.filter(output => {
                    // if (output.id === panelId && output.language && !language) {
                    //     state.alreadySelected = state.alreadySelected.filter(l => l !== output.language.translationKey)
                    // }
                    return output.id !== panelId
                });
                const lang = language as TranslatableAndSpeakableLanguage;
                const index = filtered.push({
                    id: panelId,
                    device: device,
                    language: language,
                    gain: gain,
                    pace: state.outputsPace,
                    configComplete: !!(device && language),
                    isExtra: panelId !== 'panel_1',
                    textOnly: action.payload.textOnly,
                    voiceIndex: DEFAULT_VOICE_INDEX,
                    sessionDuration: state.sessionDurationInMins
                } as Output);
                state.outputs = filtered;

                // state.transcriptionPanels = state.transcriptionPanels.map(panel => {
                //     if (panel.id === panelId) {
                //         panel.textOnly = action.payload.textOnly || false;
                //     }
                //     return panel
                // })

                // if (language && !state.alreadySelected.includes(language?.translationKey)) {
                //     state.alreadySelected.push(language.translationKey)
                // }

                if (lang?.translationKey) {
                    try {
                        getSessionContext()?.getText2Speech(lang?.translationKey)?.setOutputVolume(gain);
                    } catch (error) {

                    }

                    const selected = language as TranslatableAndSpeakableLanguage;
                    const preSelectedOutputLanguageId = selected.translationKey + ':' + selected.speakKey;

                    //UPDATING CONFIG
                    const preSelectedOutputLanguages = state.preSelectedConfig.outputLanguages || {};
                    // delete preSelectedOutputLanguages[panelId]; //first delete
                    preSelectedOutputLanguages[panelId] = preSelectedOutputLanguageId;
                    state.preSelectedConfig = {...state.preSelectedConfig, outputLanguages: preSelectedOutputLanguages};
                    localStorage.setItem('preSelectedConfig', JSON.stringify(state.preSelectedConfig));

                    //UPDATING particular value
                    state.preSelectedOutputLanguages = preSelectedOutputLanguages;
                } else {
                    //UPDATING CONFIG
                    const preSelectedOutputLanguages = state.preSelectedConfig.outputLanguages || {};
                    delete preSelectedOutputLanguages[panelId]; //delete
                    state.preSelectedConfig = {...state.preSelectedConfig, outputLanguages: preSelectedOutputLanguages};
                    localStorage.setItem('preSelectedConfig', JSON.stringify(state.preSelectedConfig));

                    //UPDATING particular value
                    state.preSelectedOutputLanguages = preSelectedOutputLanguages;
                }

                if (device?.deviceId) {
                    //UPDATING CONFIG
                    const preSelectedOutputDevices = state.preSelectedConfig.outputDevices || {};
                    // delete preSelectedOutputDevices[panelId];
                    preSelectedOutputDevices[panelId] = device.deviceId;

                    state.preSelectedConfig = {...state.preSelectedConfig, outputDevices: preSelectedOutputDevices};
                    localStorage.setItem('preSelectedConfig', JSON.stringify(state.preSelectedConfig));

                    //UPDATING particular value
                    state.preSelectedOutputDevices = preSelectedOutputDevices;
                } else {
                    //UPDATING CONFIG
                    const preSelectedOutputDevices = state.preSelectedConfig.outputDevices || {};
                    delete preSelectedOutputDevices[panelId]; //delete
                    state.preSelectedConfig = {...state.preSelectedConfig, outputDevices: preSelectedOutputDevices};
                    localStorage.setItem('preSelectedConfig', JSON.stringify(state.preSelectedConfig));

                    //UPDATING particular value
                    state.preSelectedOutputDevices = preSelectedOutputDevices;
                }
            }
        },

        updateOutputsPace(state, action: PayloadAction<number | null>) {
            const pace = action.payload || 1.0; //always set a default value

            //update all outputs
            state.outputs = state.outputs?.map(output => {
                try {
                    getSessionContext()?.getText2Speech(output.language?.translationKey)?.setOutputPace(pace);
                } catch (error) {

                }
                return {...output, pace};
            });

            //save new value
            state.outputsPace = pace;
        },
        updateAutoDetectInoutLang(state, action: PayloadAction<boolean>) {
            state.autoDetectLang = action.payload;
        },

        updateSessionDuration(state, action: PayloadAction<number>) {
            state.sessionDurationInMins = action.payload;
            state.preSelectedConfig = {...state.preSelectedConfig, sessionDuration: action.payload};
            localStorage.setItem('preSelectedConfig', JSON.stringify(state.preSelectedConfig));
        },

        updateBroadcastState(state, action: PayloadAction<boolean>) {
            state.broadcastEnabled = action.payload;
        },

        updateModelType(state, action: PayloadAction<string>) {
            state.modelType = action.payload;
        },


        addPartiallyRecognizedText(state, action: PayloadAction<string>) {
            state.currentRecognition = action.payload;
        },
        addRecognizedText(state, action: PayloadAction<Recognition>) {
            let recognitions = (state.recognizedTexts || [])
                .filter(recognition => {
                    return recognition.timestamp > (Date.now() - MAX_HISTORY_DURATION_IN_MILLIS);
                });
            recognitions.push(action.payload);

            state.recognizedTexts = recognitions;
            state.currentRecognition = null;
        },
        addPartiallyTranslatedText(state, action: PayloadAction<Map<Language, TranslationResult>>) {
            let payload = action.payload;
            payload.forEach(((translationResult, lang) => {
                // console.log('updating reducer state: ', translationResult, lang);
                state.currentTranslations.set(lang.id, translationResult.translatedText);
            }));
        },
        addTranslatedText(state, action: PayloadAction<TranslationsBody>) {
            let payload = action.payload;

            Object.keys(payload.outputs).forEach(((lang) => {
                if (!state.translations.has(lang))
                    state.translations.set(lang, [{timestamp: Date.now(), text: payload.outputs[lang]}]);
                else {
                    let translations = (state.translations.get(lang) || [])
                        .filter(translation => translation.timestamp > (Date.now() - MAX_HISTORY_DURATION_IN_MILLIS));
                    translations.push({timestamp: Date.now(), text: payload.outputs[lang]});

                    state.translations.set(lang, translations);
                }
            }));
            state.currentTranslations = new Map<string, string>();
        },

        //==== MOCKS ====
        addMockRecognizedText(state, action: PayloadAction<Recognition>) {
            const recognizedText = action.payload;
            state.recognizedTexts.push(recognizedText)
        },
        addMockTranslations(state, action: PayloadAction<number>) {
            const recognizedTextId = action.payload;

            const recognition = state.recognizedTexts.find(recognizedT => recognizedT.id === recognizedTextId);
            if (!recognition) return;

            const esT = {
                originalText: recognition.text,
                translatedText: '(es) ' + recognition.text,
                status: 'Ok',
                language: dummyEsLanguage
            } as TranslationResult;

            const enT = {
                originalText: recognition.text,
                translatedText: '(en) ' + recognition.text,
                status: 'Ok',
                language: dummyEnLanguage
            } as TranslationResult;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(validateAndStartSession.pending, (state) => {
            // console.log('startSession.pending!')
        })
        builder.addCase(validateAndStartSession.fulfilled, (state, {payload}) => {
            // console.log("fin 0.5", payload)
            state.currentSessionId = payload.sessionId;
            state.currentSessionAPIToken = payload.sessionSpeechToken;
            // console.log('startSession.fulfilled!', payload, state.currentSessionId)
        })
        builder.addCase(validateAndStartSession.rejected, (state, {payload}) => {
            // console.log('startSession.rejected!', payload)
        })

        builder.addCase(stopCurrentSession.pending, (state) => {
            // console.log('stopCurrentSession.pending!')
        })
        builder.addCase(stopCurrentSession.fulfilled, (state, {payload}) => {
            state.currentSessionId = null;
            state.currentSessionAPIToken = null;
            // console.log('stopCurrentSession.fulfilled!', payload)
        })
        builder.addCase(stopCurrentSession.rejected, (state, {payload}) => {
            // console.log('stopCurrentSession.rejected!', payload)
        })

        builder.addCase(sendTranslations.pending, (state) => {
            // console.log('sendTranslations.pending!')
        })
        builder.addCase(sendTranslations.fulfilled, (state, {payload}) => {
            // console.log('sendTranslations.fulfilled!', payload)
        })
        builder.addCase(sendTranslations.rejected, (state, {payload}) => {
            // console.log('sendTranslations.rejected!', payload)
        })

        builder.addCase(updateSession.pending, (state) => {
            // console.log('updateSession.pending!')
        })
        builder.addCase(updateSession.fulfilled, (state, {payload}) => {
            // console.log('updateSession.fulfilled!', payload)
        })
        builder.addCase(updateSession.rejected, (state, {payload}) => {
            // console.log('updateSession.rejected!', payload)
        })
    }
});

export default sessionSlice.reducer;
export const {
    setApiKey,
    updateCanStartSession,
    updateSessionState,
    setInitialOutputDevicesAmount,

    updateOutputsPace,
    updateAutoDetectInoutLang,
    updateSessionDuration,
    updateBroadcastState,

    updateModelType,

    addPanel,
    removePanel,
    updatePanelConfiguration,

    addPartiallyRecognizedText,
    addRecognizedText,
    addPartiallyTranslatedText,
    addTranslatedText,

    addMockRecognizedText,
    addMockTranslations
} = sessionSlice.actions;
